import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLimitedShellCan } from '../../../lib/shell-mode';
import { testingApi, testingKeys } from '../api';

/**
 * Lỗi mà một việc đang sửa — docs/plan-testing-tab.md §9.2.
 *
 * ## Vì sao KHÔNG hiện gì khi việc này không sửa lỗi nào
 *
 * Chưa tới 5% task là việc sửa lỗi. Một dòng "Không sửa lỗi nào" trên 95% số
 * task là nhiễu ở đúng chỗ người dùng đang đọc chi tiết một việc bình thường.
 *
 * ## Vì sao gọi riêng một endpoint
 *
 * Thêm trường vào `TaskView` sẽ bắt mọi truy vấn danh sách công việc gánh một
 * cột join cho một tính năng của vài dòng. Lời gọi này chỉ chạy khi hộp chi tiết
 * mở, và lặng lẽ không hiện gì nếu người xem không có `test:read`.
 *
 * ## ⚠️ Không nhận `asOf`
 *
 * Luật #7 áp cho view vẽ một **đại lượng biến thiên theo thời gian**. Khối này
 * liệt kê quan hệ *đang có* giữa một việc và các lỗi nó sửa — không có trục
 * thời gian nào để tua, cùng loại với `org-chart-tree`. Trạng thái từng lỗi thì
 * biến thiên, nhưng nơi đọc nó theo thời gian là báo cáo kiểm thử
 * (`testing-report-section`, đã nhận `asOf`), không phải hộp chi tiết việc.
 *
 * ## Vì sao nhắc "task xong không đóng lỗi"
 *
 * Đây là chỗ hiểu sai tốn kém nhất (§9.1): dev kéo việc sang Xong rồi tưởng lỗi
 * đã hết. Câu nhắc đứng ngay cạnh danh sách lỗi, không nằm trong một trang trợ
 * giúp mà không ai mở.
 */
export function TaskBugLinks({
  projectId,
  taskId,
}: {
  readonly projectId: string;
  readonly taskId: string;
}): ReactNode {
  const { t } = useTranslation();
  // Limited shell (APM-PSPACE-g): no `test:read` here ⇒ don't make the call at all.
  const mayRead = useLimitedShellCan('test:read', projectId) === true;

  const bugs = useQuery({
    queryKey: testingKeys.bugsForTask(projectId, taskId),
    queryFn: () => testingApi.bugsForTask(projectId, taskId),
    // Người xem không có `test:read` nhận 403; đây là một khối phụ nên nó im
    // lặng biến mất thay vì hiện một thẻ lỗi cạnh nội dung chính.
    retry: false,
    enabled: mayRead,
  });

  if (!bugs.data || bugs.data.length === 0) return null;

  return (
    <section className="flex flex-col gap-2 border-t border-border pt-3">
      <h4 className="text-sm font-medium">{t('testing.bugsFixedHere')}</h4>

      <ul className="flex flex-col gap-1">
        {bugs.data.map((bug) => (
          <li
            key={bug.id}
            className="flex flex-wrap items-center gap-2 rounded-DEFAULT border border-border px-2 py-1.5 text-xs"
          >
            <span className="font-mono text-muted-foreground">#{bug.code}</span>
            <span className="min-w-0 flex-1 truncate">{bug.title}</span>
            <span className="text-muted-foreground">{t(`testing.bugState.${bug.state}`)}</span>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">{t('testing.taskDoneNotClosed')}</p>
    </section>
  );
}
